import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { Contact } from '@app/core/business/contact/contact';
import { environment } from '@env/environment';
import { ContactPreferences } from '@app/core/business/contact/contact-preference';

@Injectable({
  providedIn: 'root',
})
export class ContactService {
  private baseUrl: string = environment.baseApiUrl;

  constructor(private http: HttpClient) {}

  // Duplicate of postCallbackRequest?
  public postRequest(
    companyCode: string,
    contactCode: string,
    request: string
  ) {
    if (environment.useMock) {
      return;
    }
    if (companyCode && contactCode && request) {
      return this.http.post(
        `${
          this.baseUrl
        }/Companies/${companyCode}/Contacts/${contactCode}/Request`,
        request
      );
    }
  }

  public postCallbackRequest(
    companyCode: string,
    contactCode: string,
    customerCode: string
  ) {
    if (environment.useMock) {
      return;
    }
    if (companyCode && contactCode && customerCode) {
      // Add customerCode to query string if it is available
      return this.http.post(
        `${
          this.baseUrl
        }/Companies/${companyCode}/Contacts/${contactCode}/CallbackRequest`,
        {},
        {
          params: {
            customerCode: customerCode,
          },
        }
      );
    }
  }

  public getTests(): Observable<Contact[]> {
    const rows: Contact[] = new Array<Contact>();

    const names = ['Robert', 'Gérard', 'Jean-Séraphin', 'Rosie', 'Matt'];
    const lastNames = ['Damon', 'Robot', 'Hatohara', 'Kurd', 'Cruz'];

    for (let i = 0; i <= 40; i++) {
      rows.push({
        code: 'Contact-' + Math.floor(Math.random() * 1000),
        companyCode: 'Company-' + Math.floor(Math.random() * 1000),
        firstName: names[Math.floor(Math.random() * 5)],
        lastName: lastNames[Math.floor(Math.random() * 5)],
        email: 'test@yopmail.com',
      });
    }

    return of(rows).pipe(delay(1000));
  }

  public getContact(
    companyCode: string,
    contactCode: string
  ): Observable<Contact> {
    if (environment.useMock) {
      const contact: Contact = {
        code: contactCode,
        companyCode: companyCode,
        email: companyCode + '@yopmail.com',
        firstName: 'Homer',
        lastName: 'Odyssée',
        preferences: {
          offerListHiddenColumns: ['code'],
          offerListVisibleColumns: [],
          offerSchedulerHiddenColumns: [],
          offerSchedulerVisibleColumns: [],
          offerQuickEntryHiddenColumns: [],
          offerQuickEntryVisibleColumns: [],
        },
      };

      return of(contact).pipe(delay(400));
    }

    return this.http.get<Contact>(
      `${this.baseUrl}/companies/${companyCode}/contacts/${contactCode}`
    );
  }

  public getPreferences(
    companyCode: string,
    contactCode: string
  ): Observable<ContactPreferences> {
    if (environment.useMock) {
      const preferences = {
        offerListHiddenColumns: ['code', 'additionalInfo'],
        offerListVisibleColumns: [
          'brand',
          'fullLabel',
          'origin',
          'price',
          'weight',
          'outer',
        ],
        offerSchedulerHiddenColumns: ['code', 'additionalInfo'],
        offerSchedulerVisibleColumns: [
          'brand',
          'fullLabel',
          'origin',
          'price',
          'weight',
          'outer',
        ],
        offerQuickEntryHiddenColumns: ['code', 'additionalInfo'],
        offerQuickEntryVisibleColumns: [
          'brand',
          'fullLabel',
          'origin',
          'price',
          'weight',
          'outer',
        ],
      };

      return of(preferences).pipe(delay(350));
    }

    return this.http.get<ContactPreferences>(
      `${
        this.baseUrl
      }/companies/${companyCode}/contacts/${contactCode}/preferences`
    );
  }

  public putPreferences(
    companyCode: string,
    contactCode: string,
    preferences: ContactPreferences
  ): Observable<any> {
    if (environment.useMock) {
      return of(true).pipe(delay(100));
    }
    return this.http.put(
      `${
        this.baseUrl
      }/companies/${companyCode}/contacts/${contactCode}/preferences`,
      preferences
    );
  }
}
